import type { CupomValidado } from '../api'
import { emCentavos } from './cashback-pedido'
import { formatarBRL, formatarPercentual } from './formato'

export const motivosDoCupomFora = ['SO_ENTREGA', 'FRETE_JA_GRATIS'] as const

export type MotivoDoCupomFora = (typeof motivosDoCupomFora)[number]

export const TEXTO_DO_CUPOM_FORA: Record<MotivoDoCupomFora, string> = {
  SO_ENTREGA: 'Este cupom vale só para entrega',
  FRETE_JA_GRATIS: 'Seu pedido já tem frete grátis',
}

export const TEXTO_FRETE_ALTERADO = 'O valor da entrega mudou. Confira o total e envie o pedido de novo.'

export type CupomDoFrete = Pick<CupomValidado, 'tipoDesconto' | 'valorDoCupom'>

export interface EntradaFreteNaTela {
  entregaEmCasa: boolean
  frete: number | null
  cupom: CupomDoFrete | null
  subtotal?: number
  freteGratisAPartirDe?: number | null
}

export interface FreteNaTela {
  freteComDesconto: boolean
  freteCobrado: number
  cupomFora: MotivoDoCupomFora | null
}

const TIPOS_DE_CUPOM_DE_FRETE: ReadonlyArray<CupomValidado['tipoDesconto']> = ['FRETE_GRATIS', 'FRETE_PERCENTUAL', 'FRETE_VALOR']

export function ehCupomDeFrete(tipo: CupomValidado['tipoDesconto'] | undefined): boolean {
  return tipo !== undefined && TIPOS_DE_CUPOM_DE_FRETE.includes(tipo)
}

export function freteNaTela({ entregaEmCasa, frete, cupom, subtotal = 0, freteGratisAPartirDe = null }: EntradaFreteNaTela): FreteNaTela {
  const cupomDeFrete = cupom && ehCupomDeFrete(cupom.tipoDesconto) ? cupom : null

  if (!entregaEmCasa) return { freteComDesconto: false, freteCobrado: 0, cupomFora: cupomDeFrete ? 'SO_ENTREGA' : null }
  if (frete === null) return { freteComDesconto: false, freteCobrado: 0, cupomFora: null }
  const centavos = emCentavos(frete)
  if (centavos > 0 && atingiuOFreteGratis(subtotal, freteGratisAPartirDe)) {
    return { freteComDesconto: true, freteCobrado: 0, cupomFora: cupomDeFrete ? 'FRETE_JA_GRATIS' : null }
  }
  if (!cupomDeFrete) return { freteComDesconto: false, freteCobrado: frete, cupomFora: null }
  if (centavos <= 0) return { freteComDesconto: false, freteCobrado: frete, cupomFora: 'FRETE_JA_GRATIS' }
  return { freteComDesconto: true, freteCobrado: (centavos - descontoEmCentavos(cupomDeFrete, centavos)) / 100, cupomFora: null }
}

function atingiuOFreteGratis(subtotal: number, freteGratisAPartirDe: number | null): boolean {
  return freteGratisAPartirDe != null && emCentavos(subtotal) >= emCentavos(freteGratisAPartirDe)
}

function descontoEmCentavos(cupom: CupomDoFrete, frete: number): number {
  const valor = cupom.valorDoCupom ?? 0
  if (cupom.tipoDesconto === 'FRETE_PERCENTUAL') return Math.min(frete, Math.max(0, Math.round((frete * valor) / 100)))
  if (cupom.tipoDesconto === 'FRETE_VALOR') return Math.min(frete, Math.max(0, emCentavos(valor)))
  return frete
}

export function textoDoCupomDeFrete(cupom: CupomDoFrete): string {
  if (cupom.tipoDesconto === 'FRETE_PERCENTUAL') return `${formatarPercentual(cupom.valorDoCupom ?? 0)} de desconto no frete`
  if (cupom.tipoDesconto === 'FRETE_VALOR') return `${formatarBRL(cupom.valorDoCupom ?? 0)} de desconto no frete`
  return 'frete grátis'
}
